<script setup lang="ts">
import { computed, defineProps } from 'vue'

import getEnv from '@/utils/env'
import { clearAuthenticationRedirect, redirectToLogin } from '@/utils/authentication'
import CustomButton from '@/components/UIElements/CustomButton.vue';
import BackgroundGradiant from '@/components/UIElements/BackgroundGradiant.vue';

const props = defineProps<{
  status?: string
  reason?: string
}>()

const requiresVerification = computed(() => props.reason === 'identity-email-not-verified')
const title = computed(() => props.status === '403' ? 'Access denied' : 'Unable to load your account')
const message = computed(() => {
  if (requiresVerification.value) {
    return 'Your session is valid, but your email address must be verified before you can continue.'
  }

  if (props.status === '403') {
    return 'Your session is valid, but you do not have access to this application.'
  }

  return 'The application could not load your account. Please try again later.'
})
const verificationUrl = computed(() => {
  const portal = getEnv('VUE_APP_AUTH_PORTAL_URL')
  const url = new URL('/verify', `${portal.replace(/\/$/, '')}/`)
  url.searchParams.set('returnTo', new URL('/projects', window.location.origin).toString())

  return url.toString()
})

function verifyAgain(): void {
  window.location.href = verificationUrl.value
}

function signInAgain(): void {
  clearAuthenticationRedirect()
  redirectToLogin(new URL('/projects', window.location.origin).toString())
}
</script>

<template>
  <div class="full-view">
    <BackgroundGradiant />
    <div class="pa-6 bar ma-auto">
        <div class="mb-2 token-text-h4">{{ title }}</div>
        <div class="mb-4">{{ message }}</div>
        <CustomButton class="mx-auto" v-if="requiresVerification" :level="$enums.ButtonLevels.Primary" @click="verifyAgain" text="Verify email address" />
        <CustomButton class="mx-auto" v-else :level="$enums.ButtonLevels.Primary" @click="signInAgain" text="Sign in again" />
    </div>
  </div>
</template>
